import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, Icon, Text } from '@components'
import type { IconName } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatTime } from '@utils'
import type { ScanCheckInResult } from '@hooks'

interface ScanResultProps {
  result: ScanCheckInResult
  registering: boolean
  onRegister: () => void
  onContinue: () => void
}

/**
 * The scan screen's full-screen outcome — shared by native and web so the two
 * only differ in how they get a QR string (camera SDK vs. browser APIs).
 */
export function ScanResult({ result, registering, onRegister, onContinue }: ScanResultProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  const presentation: Record<ScanCheckInResult['status'], { icon: IconName; color: string }> = {
    checked_in: { icon: 'check', color: theme.colors.green },
    already_checked_in: { icon: 'check', color: theme.colors.orange },
    not_registered: { icon: 'cross', color: theme.colors.error },
    wrong_event: { icon: 'alertTriangle', color: theme.colors.orange },
    invalid: { icon: 'alertTriangle', color: theme.colors.error },
    full: { icon: 'alertTriangle', color: theme.colors.orange },
    closed: { icon: 'alertTriangle', color: theme.colors.orange },
    payment_required: { icon: 'alertTriangle', color: theme.colors.orange },
  }
  const { icon, color } = presentation[result.status]

  const title = (() => {
    switch (result.status) {
      case 'checked_in':
        return t('events.checkin.scan.checkedInTitle', {
          name: `${result.firstName} ${result.lastName}`.trim(),
        })
      case 'already_checked_in':
        return t(
          result.sameDevice ? 'events.checkin.scan.alreadyTitle' : 'events.checkin.scan.alreadyTitleOtherDevice',
          {
            name: `${result.firstName} ${result.lastName}`.trim(),
            time: formatTime(new Date(result.checkedInAt)),
          },
        )
      case 'not_registered':
        return t('events.checkin.scan.notRegisteredTitle')
      case 'wrong_event':
        return t('events.checkin.scan.wrongEventTitle')
      default:
        return t('events.checkin.scan.invalidTitle')
    }
  })()

  return (
    <View style={styles.result} testID="scan-result">
      <Icon name={icon} size={72} color={color} />
      <Text type="titleBigBold" color={color} center style={styles.resultTitle}>
        {title}
      </Text>
      {result.status === 'not_registered' && result.canRegister ? (
        <Button
          title={
            registering ? t('events.checkin.scan.registering') : t('events.checkin.scan.register')
          }
          testID="scan-register"
          disabled={registering}
          onPress={onRegister}
          containerStyle={styles.fullWidth}
        />
      ) : null}
      <Button
        title={t('events.checkin.scan.continueScanning')}
        testID="scan-continue"
        unfilled
        onPress={onContinue}
        containerStyle={styles.fullWidth}
      />
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    result: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.lg,
      paddingVertical: theme.spacing.xxl,
    },
    resultTitle: { paddingHorizontal: theme.spacing.lg },
    fullWidth: { width: '100%' },
  })
